import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  NegotiatedProcedureGround,
  type ProcedureJustificationDto,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";

const TEXT_DEBOUNCE_MS = 400;

const limitedRationaleToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

const GROUNDS = [
  NegotiatedProcedureGround.NeedsAdaptationOfAvailableSolutions,
  NegotiatedProcedureGround.DesignOrInnovation,
  NegotiatedProcedureGround.NatureComplexityLegalOrFinancialCircumstances,
  NegotiatedProcedureGround.SpecificationsCannotBeEstablishedPrecisely,
];

interface Props {
  eventId: string;
  justification: ProcedureJustificationDto | null;
  noNegotiationNecessary: boolean | null;
  /** False under forsyningsforskriften § 9-1 (1), which needs no ground for the procedure. */
  requiresJustification: boolean;
  offersAwardOnInitialTenders: boolean;
}

// FOA § 13-2 requires a stated ground before konkurranse med forhandling may be used at all, and
// § 23-7 lets the buyer reserve the right to award on the initial tenders. Both are announced and
// frozen at publication, and the justification is what protokoll § 2 reads.
export const NegotiationTermsField = ({
  eventId,
  justification,
  noNegotiationNecessary,
  requiresJustification,
  offersAwardOnInitialTenders,
}: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);

  // The ground carries legal weight, so it is never inferred: both fields travel together on every
  // save, and the rationale cannot be written until the buyer has chosen a ground themselves.
  const ground = justification?.ground ?? null;

  const setGround = (chosen: NegotiatedProcedureGround) => {
    updateField({
      publicContext: {
        procedureJustification: { ground: chosen, rationale: justification?.rationale ?? "" },
      },
    });
  };

  const setRationale = (rationale: string) => {
    if (ground == null) return;
    updateField(
      { publicContext: { procedureJustification: { ground, rationale } } },
      { debounceMs: TEXT_DEBOUNCE_MS },
    );
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Heading as="h3" size="sm">
        {requiresJustification
          ? t("Grounds for using competitive procedure with negotiation")
          : t("Negotiation terms")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mt="1">
        {requiresJustification
          ? t("The procedure may only be used when one of the conditions in FOA § 13-2 is met.")
          : t("Permitted without a separate justification — forsyningsforskriften § 9-1")}
      </Text>

      {requiresJustification && (
      <>
      <FormControl mt="5">
        <FormLabel fontSize="sm">{t("Which condition applies?")}</FormLabel>
        <RadioGroup value={justification?.ground ?? ""} onChange={(v) => setGround(v as NegotiatedProcedureGround)}>
          <Stack spacing="2">
            {GROUNDS.map((ground) => (
              <Radio key={ground} value={ground} colorScheme="teal" alignItems="flex-start">
                <Text fontSize="sm">{t(ground)}</Text>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>

      <FormControl mt="5">
        <FormLabel fontSize="sm">{t("Justification")}</FormLabel>
        {ground == null ? (
          <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="4">
            <Text fontSize="sm" color="smMuted">
              {t("Choose which condition applies before writing the justification.")}
            </Text>
          </Box>
        ) : (
          <TipTap
            content={justification?.rationale ?? ""}
            onUpdate={setRationale}
            toolbar={limitedRationaleToolbar}
            debounceWait={0}
          />
        )}
        <FormHelperText>
          {t("Carried into the procurement protocol § 2 — you will not be asked to write it again.")}
        </FormHelperText>
      </FormControl>

      </>
      )}

      {offersAwardOnInitialTenders && (
        <FormControl mt="6" display="flex" alignItems="flex-start" justifyContent="space-between" gap="6">
          <Box>
            <FormLabel htmlFor="noNegotiationNecessary" mb="1" cursor="pointer">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Reserve the right to award on the initial tenders")}
              </Text>
            </FormLabel>
            <Text fontSize="sm" color="smMuted">
              {noNegotiationNecessary
                ? t("Initial tenders must be able to stand as final tenders. This is stated in the notice.")
                : t("Negotiation rounds will be held before final tenders are requested.")}
            </Text>
          </Box>
          <Switch
            id="noNegotiationNecessary"
            isChecked={noNegotiationNecessary === true}
            onChange={(e) => updateField({ publicContext: { noNegotiationNecessary: e.target.checked } })}
            colorScheme="teal"
            mt="1"
          />
        </FormControl>
      )}
    </Box>
  );
};
